/* Inline Quick Add panel — replaces drawer-based quick shop with an
   in-card variant picker. Uses existing <product-form> for cart submit. */

class InlineQuickAdd extends HTMLElement {
  constructor() {
    super();
    this._onTriggerClick = this._onTriggerClick.bind(this);
    this._onDocClick = this._onDocClick.bind(this);
    this._onValueClick = this._onValueClick.bind(this);
    this._onClose = this._onClose.bind(this);
  }

  connectedCallback() {
    this.card = this.closest('product-card, .product-card, .js-product-card');
    if (!this.card) return;

    const variantsScript = this.querySelector('[data-iqa-variants]');
    try {
      this.variants = JSON.parse(variantsScript.textContent);
    } catch (e) {
      this.variants = [];
    }

    this.variantInput = this.querySelector('[data-iqa-variant-id]');
    this.submitBtn = this.querySelector('[data-iqa-submit]');
    this.submitText = this.querySelector('[data-iqa-submit-text]');
    this.optionEls = Array.from(this.querySelectorAll('.inline-quick-add__option'));
    this.i18n = {
      addToCart: this.dataset.i18nAddToCart || window.theme?.strings?.addToCart || 'Add to cart',
      soldOut: this.dataset.i18nSoldOut || window.theme?.strings?.soldOut || 'Sold out',
    };

    // Initialise selection: prefer card-supplied variant id, fall back to input/available/first
    const cardInitialId = parseInt(this.getAttribute('data-iqa-initial-variant'), 10);
    const inputId = parseInt(this.variantInput.value, 10);
    const initial = (cardInitialId && this.variants.find(v => v.id === cardInitialId))
      || this.variants.find(v => v.id === inputId)
      || this.variants.find(v => v.available)
      || this.variants[0];
    this.selected = initial ? initial.options.slice() : this.optionEls.map(() => null);
    if (initial) this.variantInput.value = initial.id;

    this._renderSelection();
    this._updateAvailability();
    this._updateVariant();

    this.triggers = this.card.querySelectorAll('[data-inline-quick-add-trigger]');
    this.triggers.forEach(t => t.addEventListener('click', this._onTriggerClick));

    this.addEventListener('click', this._onValueClick);
    const closeBtn = this.querySelector('[data-inline-quick-add-close]');
    if (closeBtn) closeBtn.addEventListener('click', this._onClose);

    document.addEventListener('click', this._onDocClick);

    // After successful add, close the panel
    this.addEventListener('cart:added', this._onClose);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocClick);
    if (this.triggers) {
      this.triggers.forEach(t => t.removeEventListener('click', this._onTriggerClick));
    }
  }

  _onTriggerClick(e) {
    e.preventDefault();
    e.stopPropagation();
    this.toggle();
  }

  _onDocClick(e) {
    if (!this.isOpen) return;
    if (this.contains(e.target)) return;
    if (e.target.closest('[data-inline-quick-add-trigger]')) return;
    this.close();
  }

  _onClose(e) {
    if (e) e.preventDefault();
    this.close();
  }

  _onValueClick(e) {
    const btn = e.target.closest('[data-iqa-value]');
    if (!btn) return;
    e.preventDefault();
    if (btn.disabled) return;

    const optionEl = btn.closest('.inline-quick-add__option');
    const idx = this.optionEls.indexOf(optionEl);
    if (idx < 0) return;

    this.selected[idx] = btn.dataset.iqaValue;
    this._renderSelection();
    this._updateAvailability();
    this._updateVariant();
  }

  _renderSelection() {
    this.optionEls.forEach((opt, idx) => {
      const value = this.selected[idx];
      const valueLabel = opt.querySelector('[data-iqa-selected-value]');
      if (valueLabel) valueLabel.textContent = value || '';
      opt.querySelectorAll('[data-iqa-value]').forEach(b => {
        b.classList.toggle('is-selected', b.dataset.iqaValue === value);
        b.setAttribute('aria-pressed', b.dataset.iqaValue === value ? 'true' : 'false');
      });
    });
  }

  _updateAvailability() {
    // For each option, mark a value as unavailable if no variant matches the
    // already-chosen values from earlier options AND this value is in stock.
    this.optionEls.forEach((opt, idx) => {
      opt.querySelectorAll('[data-iqa-value]').forEach(btn => {
        const candidate = this.selected.slice();
        candidate[idx] = btn.dataset.iqaValue;
        const match = this.variants.find(v => {
          for (let i = 0; i <= idx; i++) {
            if (candidate[i] != null && v.options[i] !== candidate[i]) return false;
          }
          return v.available;
        });
        const unavailable = !match;
        btn.classList.toggle('is-unavailable', unavailable);
        btn.disabled = unavailable;
      });
    });
  }

  _updateVariant() {
    const match = this.variants.find(v =>
      v.options.every((opt, i) => this.selected[i] == null || opt === this.selected[i])
    );
    const allChosen = this.selected.every(v => v != null);
    const exact = allChosen
      ? this.variants.find(v => v.options.every((opt, i) => opt === this.selected[i]))
      : null;

    if (exact) {
      this.variantInput.value = exact.id;
      this.submitBtn.disabled = !exact.available;
      if (this.submitText) {
        this.submitText.textContent = exact.available ? this.i18n.addToCart : this.i18n.soldOut;
      }
    } else {
      this.submitBtn.disabled = true;
      if (this.submitText) {
        this.submitText.textContent = this.i18n.addToCart;
      }
      if (match) this.variantInput.value = match.id;
    }
  }

  toggle() { this.isOpen ? this.close() : this.open(); }

  open() {
    // Close any other open panels
    document.querySelectorAll('inline-quick-add.is-open').forEach(el => {
      if (el !== this) el.close();
    });
    this.hidden = false;
    requestAnimationFrame(() => this.classList.add('is-open'));
    this.isOpen = true;
    this.triggers.forEach(t => t.setAttribute('aria-expanded', 'true'));
  }

  close() {
    this.classList.remove('is-open');
    this.isOpen = false;
    this.triggers.forEach(t => t.setAttribute('aria-expanded', 'false'));
    setTimeout(() => {
      if (!this.isOpen) this.hidden = true;
    }, 250);
  }
}

if (!customElements.get('inline-quick-add')) {
  customElements.define('inline-quick-add', InlineQuickAdd);
}
